/* ==========================================================================
   ExceptionalFire — search overlay enhancement layer (T-SEARCH, 2026-08-31)

   Sits ON TOP of assets/ef-search.js. That file owns open/close, the inert +
   body-scroll-lock contract, the /search/suggest.json fetch and the rendering
   of products / suggestions / collections. Nothing here re-implements any of
   it, and nothing here is required for the overlay to work — if this file
   fails to load the overlay degrades to exactly the pre-T-SEARCH behaviour.

   What it adds, to bring the panel to the reference-store standard:
     1. Recent searches (localStorage, this device only, wiped by "Clear").
     2. Keyboard navigation — Down/Up across every row, Enter to open it,
        Enter with nothing selected submits the query to /search.
     3. "See all N results" — the real count, from an alternate search view.
     4. Human product titles (the catalogue stores "Name | Standard" style
        titles; the pipe tail is merchandising noise inside a 6-row panel).
     5. Hides the footer when the renderer hid the "view all" link.

   It reads the DOM the renderer produced via a MutationObserver rather than
   patching ef-search.js, so the two files stay independently deployable.
   ========================================================================== */
(function () {
  'use strict';

  var overlay = document.getElementById('ef-search-overlay');
  var input = document.getElementById('ef-so-input');
  if (!overlay || !input) return;

  var productsBox = overlay.querySelector('#ef-so-products');
  var suggestBox = overlay.querySelector('#ef-so-suggestions');
  var collBox = overlay.querySelector('#ef-so-collections');
  var viewAll = overlay.querySelector('#ef-so-viewall');
  var viewAllLabel = overlay.querySelector('[data-so-viewall-label]');
  var foot = overlay.querySelector('#efx-so-foot');
  var recentWrap = overlay.querySelector('#efx-so-recent');
  var recentRows = overlay.querySelector('#efx-so-recent-rows');
  var clearBtn = overlay.querySelector('[data-so-clear]');
  var emptyState = overlay.querySelector('#ef-so-empty');

  var STORE_KEY = 'ef.recentSearches';
  var MAX_RECENT = 5;

  /* ---------- 1. Recent searches ------------------------------------- */

  function readRecent() {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.filter(function (t) { return typeof t === 'string' && t.trim(); }).slice(0, MAX_RECENT) : [];
    } catch (e) { return []; }
  }

  function writeRecent(list) {
    try { window.localStorage.setItem(STORE_KEY, JSON.stringify(list.slice(0, MAX_RECENT))); } catch (e) { /* private mode */ }
  }

  function rememberQuery(q) {
    q = (q || '').trim();
    if (q.length < 2) return;
    var list = readRecent().filter(function (t) { return t.toLowerCase() !== q.toLowerCase(); });
    list.unshift(q);
    writeRecent(list);
  }

  var CLOCK_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 1.8"/></svg>';

  function renderRecent() {
    if (!recentWrap || !recentRows) return;
    var list = readRecent();
    if (!list.length) { recentWrap.hidden = true; recentRows.textContent = ''; return; }
    recentRows.textContent = '';
    list.forEach(function (term) {
      var a = document.createElement('a');
      a.className = 'ef-so__row';
      a.href = '/search?q=' + encodeURIComponent(term);
      a.setAttribute('data-so-item', '');
      a.innerHTML = CLOCK_SVG;
      var span = document.createElement('span');
      span.textContent = term;          // textContent, never innerHTML — the term is user input
      a.appendChild(span);
      recentRows.appendChild(a);
    });
    recentWrap.hidden = false;
  }

  if (clearBtn) {
    clearBtn.addEventListener('click', function () {
      try { window.localStorage.removeItem(STORE_KEY); } catch (e) { /* noop */ }
      renderRecent();
      clearActive();
      input.focus();
    });
  }

  /* ---------- 2. Result-count for "See all N results" ----------------- */

  var countAbort = null;
  var countCache = Object.create(null);

  function setViewAllLabel(q, n) {
    if (!viewAllLabel) return;
    if (typeof n !== 'number' || n <= 0) { viewAllLabel.textContent = 'See all results'; return; }
    // Shopify's search caps at 1000 hits, so a flat "1,000" would be a claim we
    // cannot stand behind. Above the cap we say 1,000+.
    var shown = n >= 1000 ? '1,000+' : n.toLocaleString('en-US');
    viewAllLabel.textContent = 'See all ' + shown + ' result' + (n === 1 ? '' : 's');
  }

  function fetchCount(q) {
    setViewAllLabel(q, countCache[q]);
    if (q in countCache) return;
    if (countAbort) countAbort.abort();
    countAbort = new AbortController();
    fetch('/search?q=' + encodeURIComponent(q) + '&type=product&view=count', { signal: countAbort.signal })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(new Error(r.status)); })
      .then(function (t) {
        var n = parseInt(String(t).trim().replace(/[^0-9]/g, ''), 10);
        if (!isNaN(n)) { countCache[q] = n; if (currentQuery() === q) setViewAllLabel(q, n); }
      })
      .catch(function () { /* count is a nicety; the link still works without it */ });
  }

  function currentQuery() { return input.value.trim(); }

  /* ---------- 3. Post-render pass ------------------------------------ */

  /* The renderer asks the CDN for _112x112. On a 2x phone a 64px-wide thumb wants
     128 real pixels, so the row reads soft. Same URL, larger crop. */
  function sharpenThumbs() {
    if (!productsBox) return;
    var imgs = productsBox.querySelectorAll('.ef-so__product-img');
    for (var i = 0; i < imgs.length; i++) {
      var src = imgs[i].getAttribute('src') || '';
      if (src.indexOf('_112x112') === -1) continue;
      imgs[i].setAttribute('src', src.replace('_112x112', '_192x192'));
    }
  }

  function humaniseTitles() {
    if (!productsBox) return;
    var nodes = productsBox.querySelectorAll('.ef-so__product-title');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.dataset.soHuman) continue;
      var t = el.textContent || '';
      var human = t.split(' | ')[0].split(' - ')[0].trim();
      if (human && human !== t) el.textContent = human;
      el.dataset.soHuman = '1';
    }
  }

  function markItems() {
    var lists = [suggestBox, collBox, productsBox];
    for (var i = 0; i < lists.length; i++) {
      if (!lists[i]) continue;
      var kids = lists[i].children;
      for (var j = 0; j < kids.length; j++) {
        kids[j].setAttribute('data-so-item', '');
        kids[j].setAttribute('role', 'option');
      }
    }
  }

  function syncFoot() {
    if (!foot || !viewAll) return;
    var hidden = viewAll.style.display === 'none';
    foot.hidden = hidden;
    if (!hidden) {
      var q = currentQuery();
      viewAll.setAttribute('data-so-item', '');
      // The results page is type=product; carry that through so the destination
      // matches what the panel just previewed.
      var href = viewAll.getAttribute('href') || '';
      if (href.indexOf('type=') === -1) viewAll.setAttribute('href', href + (href.indexOf('?') === -1 ? '?' : '&') + 'type=product');
      if (q) fetchCount(q);
    }
  }

  function afterRender() {
    sharpenThumbs();
    humaniseTitles();
    markItems();
    syncFoot();
    clearActive();
    var open = overlay.getAttribute('aria-hidden') === 'false';
    var typing = currentQuery().length >= 2;
    input.setAttribute('aria-expanded', open && typing ? 'true' : 'false');
  }

  if (window.MutationObserver) {
    var mo = new MutationObserver(function () { afterRender(); });
    [productsBox, suggestBox, collBox].forEach(function (n) {
      if (n) mo.observe(n, { childList: true });
    });
    if (viewAll) mo.observe(viewAll, { attributes: true, attributeFilter: ['style'] });
  }

  /* ---------- 4. Keyboard navigation ---------------------------------- */

  function visibleItems() {
    var all = overlay.querySelectorAll('[data-so-item]');
    var out = [];
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.offsetParent !== null || el.getClientRects().length) out.push(el);
    }
    return out;
  }

  function clearActive() {
    var prev = overlay.querySelectorAll('.is-active');
    for (var i = 0; i < prev.length; i++) prev[i].classList.remove('is-active');
    input.removeAttribute('aria-activedescendant');
  }

  var soId = 0;
  function setActive(el) {
    clearActive();
    if (!el) return;
    el.classList.add('is-active');
    if (!el.id) el.id = 'efx-so-opt-' + (++soId);
    input.setAttribute('aria-activedescendant', el.id);
    if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }

  function move(delta) {
    var items = visibleItems();
    if (!items.length) return;
    var cur = -1;
    for (var i = 0; i < items.length; i++) if (items[i].classList.contains('is-active')) { cur = i; break; }
    var next = cur + delta;
    if (next < 0) { clearActive(); return; }          // back up into the input
    if (next >= items.length) next = items.length - 1;
    setActive(items[next]);
  }

  function submitQuery() {
    var q = currentQuery();
    if (!q) return;
    rememberQuery(q);
    window.location.href = '/search?q=' + encodeURIComponent(q) + '&type=product';
  }

  input.addEventListener('keydown', function (e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); return; }
    if (e.key === 'Enter') {
      var active = overlay.querySelector('[data-so-item].is-active');
      e.preventDefault();
      if (active) { rememberQuery(currentQuery()); active.click(); }
      else submitQuery();
      return;
    }
    if (e.key === 'Tab') clearActive();
  });

  // Typing invalidates the selection; ef-search.js re-renders on its own clock.
  input.addEventListener('input', function () {
    clearActive();
    if (currentQuery().length < 2 && foot) foot.hidden = true;
  });

  // A pointer landing on a row takes over the selection so the two never disagree.
  overlay.addEventListener('mousemove', function (e) {
    var row = e.target.closest ? e.target.closest('[data-so-item]') : null;
    if (row && !row.classList.contains('is-active')) setActive(row);
  });

  // Any real result click is a successful search — remember what got us there.
  overlay.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    if (e.target.closest('.ef-so__product, .ef-so__suggest-link, .ef-so__coll-link, .ef-so__view-all')) {
      rememberQuery(currentQuery());
    }
  });

  /* ---------- 5. Open/close hooks ------------------------------------- */

  if (window.MutationObserver) {
    new MutationObserver(function () {
      if (overlay.getAttribute('aria-hidden') === 'false') {
        renderRecent();
        clearActive();
        if (foot) foot.hidden = true;
        if (emptyState) emptyState.scrollTop = 0;
      } else {
        clearActive();
      }
    }).observe(overlay, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  renderRecent();
})();
